import {record, flushEvents} from "aws-amplify/analytics/kinesis-firehose";
import {fetchAuthSession} from "aws-amplify/auth";
import {PinpointCompatibleEvent} from "./pinpointCompatibility";

export interface FirehoseTransportStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

const browserStorage: FirehoseTransportStorage = {
    getItem: (key) => localStorage.getItem(key),
    setItem: (key, value) => localStorage.setItem(key, value),
    removeItem: (key) => localStorage.removeItem(key),
};

export function createFirehoseTransport(streamName: string, storage: FirehoseTransportStorage = browserStorage) {
    const queueStorageKey = `__analytics.firehose.${streamName}.queue`;
    const queuedRecords = loadQueuedRecords(storage, queueStorageKey);
    let authReady = false;
    let isDraining = false;
    let drainRequestedWhileDraining = false;
    let activeDrain: Promise<void> | undefined;

    const drain = (): Promise<void> => {
        if (activeDrain) {
            drainRequestedWhileDraining = true;
            return activeDrain;
        }

        isDraining = true;
        activeDrain = (async (): Promise<void> => {
            do {
                drainRequestedWhileDraining = false;

                let session;
                try {
                    session = await fetchAuthSession();
                } catch {
                    continue;
                }

                if (!session.tokens || !session.credentials) {
                    continue;
                }

                authReady = true;

                while (queuedRecords.length > 0) {
                    const data = queuedRecords[0];
                    try {
                        record({streamName, data});
                    } catch {
                        authReady = false;
                        break;
                    }
                    queuedRecords.shift();
                    persistQueue(storage, queueStorageKey, queuedRecords);
                }
            } while (drainRequestedWhileDraining && queuedRecords.length > 0);
        })().finally(() => {
            activeDrain = undefined;
            isDraining = false;

            if (drainRequestedWhileDraining && queuedRecords.length > 0) {
                void drain();
            }
        });

        return activeDrain;
    };

    return {
        record: (event: PinpointCompatibleEvent): void => {
            const data = new TextEncoder().encode(`${JSON.stringify(event)}\n`);

            if (authReady && !isDraining) {
                record({streamName, data});
                return;
            }

            queuedRecords.push(data);
            persistQueue(storage, queueStorageKey, queuedRecords);
            if (event.event_type !== "_session.start") {
                void drain();
            }
        },
        flush: (): void => {
            void flushAfterDraining();
        },
    };

    async function flushAfterDraining(): Promise<void> {
        do {
            await (activeDrain ?? (queuedRecords.length > 0 ? drain() : Promise.resolve()));
        } while (activeDrain);

        flushEvents();
    }
}

function loadQueuedRecords(storage: FirehoseTransportStorage, storageKey: string): Uint8Array[] {
    try {
        const storedRecords: unknown = JSON.parse(storage.getItem(storageKey) ?? "[]");
        return isStringArray(storedRecords) ? storedRecords.map((record) => new TextEncoder().encode(record)) : [];
    } catch {
        return [];
    }
}

function persistQueue(storage: FirehoseTransportStorage, storageKey: string, queuedRecords: Uint8Array[]): void {
    try {
        if (queuedRecords.length === 0) {
            storage.removeItem(storageKey);
            return;
        }

        storage.setItem(storageKey, JSON.stringify(queuedRecords.map((record) => new TextDecoder().decode(record))));
    } catch {
        // Keep the in-memory queue when browser storage is unavailable.
    }
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === "string");
}
